import { motion } from 'framer-motion';

// Shared container style for consistency
const VisualContainer = ({ children }: { children: React.ReactNode }) => (
    <div className="w-full aspect-[4/3] bg-gradient-to-br from-white to-blue-50/50 rounded-xl border border-blue-100 shadow-sm overflow-hidden relative flex items-center justify-center p-3 sm:p-6 md:p-8">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#3b82f60a_1px,transparent_1px),linear-gradient(to_bottom,#3b82f60a_1px,transparent_1px)] bg-[size:16px_16px] [mask-image:radial-gradient(ellipse_at_center,black_60%,transparent_100%)] pointer-events-none" />
        {children}
    </div>
);

// Component 1: Brand Kit Application (One-Click Compliance)
export const BrandKitVisual = () => {
    return (
        <VisualContainer>
            <div className="w-full max-w-[420px] h-[200px] sm:h-[320px] bg-white rounded-lg shadow-md border border-gray-100 flex overflow-hidden relative">

                {/* Left Panel: Design System Controls */}
                <div className="w-[100px] sm:w-[140px] bg-gray-50/80 border-r border-gray-100 flex flex-col p-3 sm:p-5 gap-3 sm:gap-5 z-10 backdrop-blur-sm">
                    <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Brand Assets</div>

                    {/* Logo Section */}
                    <div className="space-y-1.5">
                        <div className="text-[8px] font-semibold text-gray-500">Logo</div>
                        <div className="w-full aspect-square rounded-lg border border-gray-200 bg-white flex items-center justify-center shadow-sm">
                            <div className="w-8 h-8 rounded bg-blue-600 flex items-center justify-center text-white font-bold text-xs">D</div>
                        </div>
                    </div>

                    {/* Colors Section */}
                    <div className="space-y-1.5">
                        <div className="text-[8px] font-semibold text-gray-500 flex justify-between">
                            Palette <span className="text-blue-500 text-[8px]">Active</span>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            {['bg-blue-600', 'bg-indigo-500', 'bg-sky-400', 'bg-slate-800'].map((c, i) => (
                                <motion.div
                                    key={i}
                                    className={`aspect-square rounded-md ${c} shadow-sm border border-black/5`}
                                    initial={{ scale: 0.8 }}
                                    whileInView={{ scale: 1 }}
                                    transition={{ delay: i * 0.1 }}
                                />
                            ))}
                        </div>
                    </div>

                    {/* Font Section */}
                    <div className="mt-auto space-y-1.5 opacity-60">
                        <div className="text-[8px] font-semibold text-gray-400">Typography</div>
                        <div className="text-xl font-bold font-syne text-gray-800">Aa</div>
                    </div>
                </div>

                {/* Right Panel: Live Slide Preview */}
                <div className="flex-1 bg-white relative p-3 sm:p-6 flex flex-col">

                    {/* "Applying" Indicator Overlay */}
                    <motion.div
                        className="absolute inset-0 bg-blue-50/20 z-20 pointer-events-none"
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: [0, 1, 0] }}
                        transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
                    />

                    {/* Slide Header */}
                    <div className="mb-6 relative z-10">
                        <div className="flex items-center justify-between mb-2">
                            <motion.div
                                initial={{ color: "#9CA3AF" }}
                                whileInView={{ color: "#1F2937" }}
                                transition={{ duration: 1, repeat: Infinity, repeatDelay: 3, repeatType: "reverse" }}
                                className="text-sm font-bold font-syne uppercase tracking-wider"
                            >
                                Q4 Strategy
                            </motion.div>
                            {/* Tiny Logo in Slide Corner */}
                            <motion.div
                                initial={{ opacity: 0.3, scale: 0.8 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 1, repeat: Infinity, repeatDelay: 3, repeatType: "reverse" }}
                                className="w-3 h-3 bg-blue-600 rounded-sm"
                            />
                        </div>
                        <motion.div
                            initial={{ width: "20%", backgroundColor: "#E5E7EB" }}
                            whileInView={{ width: "100%", backgroundColor: "#2563EB" }}
                            transition={{ duration: 1, repeat: Infinity, repeatDelay: 3, repeatType: "reverse" }}
                            className="h-1 rounded-full"
                        />
                    </div>

                    {/* Slide Content Blocks */}
                    <div className="space-y-3 relative z-10 flex-1">
                        {[1, 2].map((i) => (
                            <div key={i} className="flex gap-3 items-start p-2 rounded border border-transparent hover:border-blue-50 transition-colors">
                                <motion.div
                                    initial={{ backgroundColor: "#D1D5DB" }}
                                    whileInView={{ backgroundColor: "#3B82F6" }}
                                    transition={{ duration: 1, delay: i * 0.2, repeat: Infinity, repeatDelay: 3, repeatType: "reverse" }}
                                    className="w-8 h-8 rounded-md shrink-0 opacity-80"
                                />
                                <div className="space-y-1.5 flex-1">
                                    <div className="h-2 w-full bg-gray-100 rounded" />
                                    <div className="h-2 w-2/3 bg-gray-50 rounded" />
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Cursor Action Simulation */}
                    <motion.div
                        className="absolute z-30 pointer-events-none"
                        initial={{ x: 200, y: 300, opacity: 0 }}
                        whileInView={{
                            x: [200, -80, -80, 200], // Move to left panel, click palette
                            y: [300, 150, 150, 300],
                            opacity: [0, 1, 1, 0]
                        }}
                        transition={{ duration: 4, repeat: Infinity, repeatDelay: 0.5 }}
                    >
                        <svg className="w-5 h-5 text-gray-900 drop-shadow-md" viewBox="0 0 24 24" fill="currentColor" stroke="white" strokeWidth="2">
                            <path d="M5.65376 12.3673H5.46026L5.31717 12.4976L0.500002 16.8829L0.500002 1.19169L11.7118 9.20839L8.14441 9.20839C7.79542 9.20839 7.50002 9.49379 7.50002 9.84278V12.0007L5.65376 12.3673Z" />
                        </svg>
                    </motion.div>

                </div>

            </div>
        </VisualContainer>
    );
};

// Component 2: Professional Polish (Draft vs Agency Scan)
export const ProfessionalPolishVisual = () => {
    return (
        <VisualContainer>
            <div className="w-full max-w-[420px] h-[200px] sm:h-[320px] bg-white rounded-lg shadow-md border border-gray-100 overflow-hidden relative">

                {/* Layer 1: The "Draft" State (Underneath) */}
                <div className="absolute inset-0 p-4 sm:p-8 flex flex-col gap-3 sm:gap-6 bg-gray-50">
                    {/* Draft Header - Sketchy */}
                    <div className="flex gap-4 items-center opacity-50">
                        <div className="w-12 h-12 rounded-full border-2 border-dashed border-gray-400" />
                        <div className="h-6 w-48 border-2 border-dashed border-gray-400 rounded" />
                    </div>
                    {/* Draft Body - Wireframe */}
                    <div className="flex-1 border-2 border-dashed border-gray-300 rounded p-4 flex gap-4 items-end justify-between">
                        {[1, 2, 3, 4].map(i => (
                            <div key={i} className="w-16 border-2 border-dashed border-gray-400 rounded-t-sm" style={{ height: `${20 + i * 15}%` }} />
                        ))}
                    </div>
                    {/* Draft Label */}
                    <div className="absolute top-4 right-4 text-xs font-mono text-gray-400 font-bold uppercase tracking-widest rot-90">
                        DRAFT_V1
                    </div>
                </div>

                {/* Layer 2: The "Polished" State (Masked Overlay) */}
                <motion.div
                    className="absolute inset-0 bg-white p-4 sm:p-8 flex flex-col gap-3 sm:gap-6 z-10"
                    initial={{ clipPath: "inset(0 0 100% 0)" }}
                    whileInView={{ clipPath: "inset(0 0 -20% 0)" }}
                    transition={{ duration: 3, repeat: Infinity, repeatDelay: 2, ease: "easeInOut" }}
                >
                    {/* Polished Header - Premium */}
                    <div className="flex gap-4 items-center">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-tr from-blue-600 to-indigo-500 shadow-lg flex items-center justify-center text-white font-bold text-lg">
                            D
                        </div>
                        <div>
                            <div className="text-xl font-bold text-gray-900 font-syne">Market Analysis</div>
                            <div className="text-[10px] text-blue-600 font-medium tracking-wide uppercase">Q3 Report</div>
                        </div>
                    </div>
                    {/* Polished Body - Rich UI */}
                    <div className="flex-1 bg-gradient-to-b from-blue-50/50 to-white rounded-xl border border-blue-100 p-6 flex gap-4 items-end justify-between shadow-inner relative overflow-hidden">
                        {/* Glossy Overlay */}
                        <div className="absolute inset-0 bg-gradient-to-tr from-white/50 to-transparent pointer-events-none" />

                        {[1, 2, 3, 4].map(i => (
                            <motion.div
                                key={i}
                                className="w-16 rounded-t-lg shadow-lg relative group"
                                style={{
                                    height: `${20 + i * 15}%`,
                                    background: `linear-gradient(to bottom, ${['#60a5fa', '#3b82f6', '#2563eb', '#1d4ed8'][i - 1]}, #1e40af)`
                                }}
                            >
                                <div className="absolute inset-0 bg-white/20 opacity-0 group-hover:opacity-100 transition-opacity" />
                            </motion.div>
                        ))}
                    </div>
                    {/* Agency Badge */}
                    <div className="absolute top-0 right-0 p-4">
                        <div className="bg-emerald-50 text-emerald-600 px-3 py-1 rounded text-[10px] font-bold border border-emerald-100 shadow-sm flex items-center gap-1.5">
                            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                            AGENCY QUALITY
                        </div>
                    </div>
                </motion.div>

                {/* The Scan Laser Line */}
                <motion.div
                    className="absolute left-0 right-0 h-[2px] bg-blue-500 shadow-[0_0_20px_2px_rgba(59,130,246,0.8)] z-20 pointer-events-none"
                    initial={{ top: "100%" }}
                    whileInView={{ top: "-20%" }}
                    transition={{ duration: 3, repeat: Infinity, repeatDelay: 2, ease: "easeInOut" }}
                >
                    <div className="absolute right-4 -top-7 bg-blue-600 text-white text-[10px] font-bold px-2 py-1 rounded shadow-sm">
                        POLISHING...
                    </div>
                </motion.div>

            </div>
        </VisualContainer>
    );
};
